import { useState } from 'react';

import { useMsal } from '@azure/msal-react';
import { notification } from 'antd';

import saveAs from 'file-saver';
import { CustomerFacility, Product, ProductSize } from '../../api-client';
import {
	AdminApiClient,
	CustomerApiClient,
	ProductApiClient,
} from '../../providers/api-provider';

export type AdminProductFilters = {
	sapCode: string;
	catalogCode: string;
	aestheticMotif: string;
	aestheticLine: string;
};

export const useProduct = () => {
	const { instance } = useMsal();

	const [productsDetail, setProductsDetail] = useState<Product>();
	const [productsDetailLoading, setProductsDetailLoading] =
		useState<boolean>(false);

	const handleProductsDetail = async (productId: number) => {
		setProductsDetailLoading(true);

		const clientApi = await ProductApiClient(instance);

		clientApi
			.getProduct(productId)
			.then((response) => {
				setProductsDetail(response.data);
			})
			.catch((reason) => {
				setProductsDetail({});

				notification.error({
					message: 'Unable to retrieve product details!',
					description: 'Unable to retrieve product details.',
					duration: 2,
				});
			})
			.finally(() => {
				setProductsDetailLoading(false);
			});
	};

	const [productPrice, setProductPrice] = useState<ProductSize>();
	const [productPriceLoading, setProductPriceLoading] =
		useState<boolean>(false);

	const handleProductPrice = async (productId: number, customerId: number) => {
		setProductPriceLoading(true);

		const clientApi = await CustomerApiClient(instance);

		clientApi
			.getCustomerProductPrice(customerId, productId)
			.then((response) => {
				setProductPrice(response.data);
			})
			.catch((reason) => {
				setProductPrice({});

				notification.error({
					message: 'Unable to retrieve product price!',
					description: 'Unable to retrieve product price.',
					duration: 2,
				});
			})
			.finally(() => {
				setProductPriceLoading(false);
			});
	};

	const [productSizes, setProductSizes] = useState<ProductSize[]>();
	const [productSizesLoading, setProductSizesLoading] =
		useState<boolean>(false);

	const handleProductSizes = async (productId: number, customerId: number) => {
		setProductSizesLoading(true);

		const clientApi = await CustomerApiClient(instance);

		clientApi
			.getCustomerProductSizes(customerId, productId)
			.then((response) => {
				setProductSizes(response.data);
			})
			.catch((reason) => {
				setProductSizes([]);

				notification.error({
					message: 'Unable to retrieve product sizes!',
					description: 'Unable to retrieve product sizes.',
					duration: 2,
				});
			})
			.finally(() => {
				setProductSizesLoading(false);
			});
	};

	const [facilities, setFacilities] = useState<CustomerFacility[]>();
	const [facilitiesLoading, setFacilitiesLoading] = useState<boolean>(false);

	const handleFacilities = async (customerId: number) => {
		setFacilitiesLoading(true);

		const clientApi = await CustomerApiClient(instance);

		clientApi
			.getCustomerFacilities(customerId)
			.then((response) => {
				setFacilities(response.data);
			})
			.catch((reason) => {
				setFacilities([]);

				notification.error({
					message: 'Unable to retrieve product facilities!',
					description: 'Unable to retrieve product facilities.',
					duration: 2,
				});
			})
			.finally(() => {
				setFacilitiesLoading(false);
			});
	};

	const [availableDate, setAvailableDate] = useState<string>();
	const [availableDateLoading, setAvailableDateLoading] =
		useState<boolean>(false);

	const handleAvailableDate = async (
		productId: number,
		facilityId: number = -1,
	) => {
		setAvailableDateLoading(true);

		const clientApi = await ProductApiClient(instance);

		clientApi
			.getProductFirstAvailableDate(productId, facilityId)
			.then((response) => {
				setAvailableDate(response.data);
			})
			.catch((reason) => {
				setAvailableDate('');

				notification.error({
					message: 'Unable to retrieve available date!',
					description: 'Unable to retrieve product first available date.',
					duration: 2,
				});
			})
			.finally(() => {
				setAvailableDateLoading(false);
			});
	};

	const [productsAvailableDate, setProductsAvailableDate] = useState<string>();
	const [productsAvailableDateLoading, setProductsAvailableDateLoading] =
		useState<boolean>(false);

	const handleProductsAvailableDate = async (
		productIds: number[],
		facilityId: number,
	) => {
		setProductsAvailableDateLoading(true);

		const clientApi = await ProductApiClient(instance);

		clientApi
			.getProductsFirstAvailableDate(facilityId, productIds)
			.then((response) => {
				setProductsAvailableDate(response.data);
			})
			.catch((reason) => {
				setProductsAvailableDate('');

				notification.error({
					message: 'Unable to retrieve available dates!',
					description: 'Unable to retrieve products first available date.',
					duration: 2,
				});
			})
			.finally(() => {
				setProductsAvailableDateLoading(false);
			});
	};

	// =========================
	// Admin area
	// =========================

	const [uploadCustomerPricesLoading, setUploadLoading] =
		useState<boolean>(false);
	const [uploadCustomerPricesError, setUploadError] = useState<string | null>(
		null,
	);
	const [uploadCustomerPricesSuccess, setUploadSuccess] =
		useState<boolean>(false);

	const resetCustomerPricesUploadState = () => {
		setUploadError(null);
		setUploadSuccess(false);
	};

	const uploadCustomerPricesExcel = async (file: File) => {
		if (!file) {
			setUploadError('Select a file.');
			setUploadSuccess(false);
			return;
		}

		try {
			setUploadLoading(true);
			setUploadError(null);
			setUploadSuccess(false);

			const adminApi = await AdminApiClient(instance);
			await adminApi.importProductCustomerPrices(file);

			setUploadSuccess(true);
		} catch (err: any) {
			const msg = err?.response?.data ?? 'Error during file import process';
			setUploadError(
				typeof msg === 'string' ? msg : 'Error during file import process',
			);

			notification.error({
				message: 'Import failed',
				description: 'Unable to import customers prices.',
				duration: 2,
			});
		} finally {
			setUploadLoading(false);
		}
	};

	const [adminProducts, setAdminProducts] = useState<ProductSize[] | undefined>(
		undefined,
	);

	const [totalAdminProductsCount, setTotalAdminProductsCount] = useState<
		number | undefined
	>(undefined);

	const [adminProductsLoading, setAdminProductsLoading] =
		useState<boolean>(false);

	const handleAdminProducts = async (
		page: number,
		enabled?: boolean,
		filters?: AdminProductFilters,
	) => {
		setAdminProductsLoading(true);

		try {
			const adminApi = await AdminApiClient(instance);
			const response = await adminApi.getAllProducts(
				page,
				enabled,
				filters?.sapCode,
				filters?.catalogCode,
				filters?.aestheticMotif,
				filters?.aestheticLine,
			);
			setAdminProducts(response.data.items);
			setTotalAdminProductsCount(response.data.total);
		} catch (err) {
			setAdminProducts([]);

			notification.error({
				message: 'Unable to retrieve products!',
				description: 'Unable to retrieve products.',
				duration: 2,
			});
		} finally {
			setAdminProductsLoading(false);
		}
	};

	type BusinessUnit = 'watches' | 'jewels';

	const [uploadProductsLoading, setUploadProductsLoading] =
		useState<boolean>(false);
	const [uploadProductsError, setUploadProductsError] = useState<string | null>(
		null,
	);
	const [uploadProductsSuccess, setUploadProductsSuccess] =
		useState<boolean>(false);

	const resetProductsUploadState = () => {
		setUploadProductsError(null);
		setUploadProductsSuccess(false);
	};

	const uploadProductsExcel = async (
		file: File,
		businessUnit: BusinessUnit,
		skiRemove: boolean = false,
	) => {
		if (!file) {
			setUploadProductsError('Select a file.');
			setUploadProductsSuccess(false);
			return;
		}

		try {
			setUploadProductsLoading(true);
			setUploadProductsError(null);
			setUploadProductsSuccess(false);

			const adminApi = await AdminApiClient(instance);
			await adminApi.importProducts(file, businessUnit, skiRemove);

			setUploadProductsSuccess(true);
		} catch (err: any) {
			const msg = err?.response?.data ?? 'Error during products import process';
			setUploadProductsError(
				typeof msg === 'string' ? msg : 'Error during products import process',
			);

			notification.error({
				message: 'Import failed',
				description: 'Unable to import products.',
				duration: 2,
			});
		} finally {
			setUploadProductsLoading(false);
		}
	};

	const [exportAdminProductsLoading, setExportAdminProductsLoading] =
		useState<boolean>(false);

	const handleExportAdminProducts = async (
		enabled?: boolean,
		filters?: AdminProductFilters,
	) => {
		setExportAdminProductsLoading(true);

		try {
			const adminApi = await AdminApiClient(instance);

			const response = await adminApi.exportProducts(
				enabled,
				filters?.sapCode,
				filters?.catalogCode,
				filters?.aestheticMotif,
				filters?.aestheticLine,
				{
					responseType: 'blob',
				},
			);

			const cd =
				response.headers?.['content-disposition'] ||
				response.headers?.['Content-Disposition'];

			let filename = `products_export_${
				new Date().toISOString().split('T')[0]
			}.xlsx`;

			const match = /filename\*=UTF-8''([^;]+)|filename="?([^"]+)"?/i.exec(
				cd || '',
			);
			if (match) {
				filename = decodeURIComponent(match[1] || match[2]);
			}

			saveAs(new Blob([response.data]), filename);
		} catch (err) {
			notification.error({
				message: 'Unable to export products!',
				description: 'Unable to export products.',
				duration: 2,
			});
		} finally {
			setExportAdminProductsLoading(false);
		}
	};

	return {
		handleProductsDetail,
		productsDetail,
		productsDetailLoading,
		handleProductPrice,
		productPrice,
		productPriceLoading,
		handleProductSizes,
		productSizes,
		productSizesLoading,
		handleFacilities,
		facilities,
		facilitiesLoading,
		handleAvailableDate,
		availableDate,
		availableDateLoading,
		handleProductsAvailableDate,
		productsAvailableDate,
		productsAvailableDateLoading,
		uploadCustomerPricesExcel,
		uploadCustomerPricesLoading,
		uploadCustomerPricesError,
		uploadCustomerPricesSuccess,
		resetCustomerPricesUploadState,
		adminProducts,
		totalAdminProductsCount,
		adminProductsLoading,
		handleAdminProducts,
		uploadProductsExcel,
		uploadProductsLoading,
		uploadProductsError,
		uploadProductsSuccess,
		resetProductsUploadState,
		exportAdminProductsLoading,
		handleExportAdminProducts,
	};
};
